import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import StockThumb from "@/components/StockThumb";
import { X, Search, Copy } from "lucide-react";

/**
 * Picker dialog: shows other catalog items that already have template yields.
 * On confirm, returns the chosen item's template_yields array via onPick.
 */
const CopyYieldsDialog = ({ open, currentId, onClose, onPick }) => {
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [chosen, setChosen] = useState(null);

  useEffect(() => {
    if (!open) return;
    setSearch("");
    setChosen(null);
    api
      .get("/stock")
      .then((r) => {
        const eligible = (r.data || []).filter(
          (it) => it.id !== currentId && Array.isArray(it.template_yields) && it.template_yields.length > 0
        );
        setItems(eligible);
      })
      .catch(() => toast.error("Could not load catalog"));
  }, [open, currentId]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter(
      (it) =>
        it.name.toLowerCase().includes(q) ||
        (it.sku || "").toLowerCase().includes(q)
    );
  }, [items, search]);

  if (!open) return null;

  const confirm = () => {
    if (!chosen) {
      toast.error("Pick an item first");
      return;
    }
    const cleaned = (chosen.template_yields || []).map((y) => ({
      name: y.name,
      qty_per_unit: Number(y.qty_per_unit) || 0,
    }));
    onPick?.(cleaned);
    toast.success(`Copied ${cleaned.length} sizes from ${chosen.name}`);
    onClose?.();
  };

  return (
    <div
      className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-4"
      data-testid="copy-yields-overlay"
      onClick={onClose}
    >
      <div
        className="nb-card p-5 w-full max-w-xl"
        onClick={(e) => e.stopPropagation()}
        data-testid="copy-yields-dialog"
      >
        <div className="flex items-start justify-between mb-3">
          <div>
            <div className="label-eyebrow">Template yields</div>
            <h3 className="font-display text-2xl mt-1">Copy from another item</h3>
          </div>
          <button
            data-testid="copy-yields-close"
            onClick={onClose}
            className="nb-btn bg-white p-2"
          >
            <X size={18} strokeWidth={2.5} />
          </button>
        </div>

        <div className="relative mb-3">
          <Search
            size={18}
            strokeWidth={2.5}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-[#607087]"
          />
          <input
            data-testid="copy-yields-search"
            className="nb-input pl-10 h-11"
            placeholder="Search items with yields…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <div
          className="max-h-[50vh] overflow-y-auto scrollbar-clean flex flex-col gap-2 pr-1"
          data-testid="copy-yields-list"
        >
          {filtered.length === 0 ? (
            <div className="text-sm text-[#607087] italic py-8 text-center">
              No other items have template yields yet.
            </div>
          ) : (
            filtered.map((it) => {
              const isChosen = chosen?.id === it.id;
              return (
                <button
                  key={it.id}
                  data-testid={`copy-yields-pick-${it.id}`}
                  onClick={() => setChosen(it)}
                  className={`flex items-center gap-3 p-3 rounded-2xl text-left transition-all ${
                    isChosen
                      ? "bg-[#228EBF]/15 border border-[#228EBF]/40 shadow-inner"
                      : "bg-white/70 border border-white hover:bg-white"
                  }`}
                  type="button"
                >
                  <StockThumb item={it} size="h-12 w-12" />
                  <div className="flex-1 min-w-0">
                    <div className="font-display text-base truncate">{it.name}</div>
                    <div className="text-xs text-[#607087] truncate">
                      {it.template_yields.length} size
                      {it.template_yields.length === 1 ? "" : "s"} ·{" "}
                      {it.template_yields
                        .slice(0, 3)
                        .map((y) => y.name)
                        .join(", ")}
                      {it.template_yields.length > 3 ? "…" : ""}
                    </div>
                  </div>
                </button>
              );
            })
          )}
        </div>

        <div className="flex justify-end gap-3 mt-4">
          <button
            data-testid="copy-yields-cancel"
            onClick={onClose}
            className="nb-btn bg-white px-4 py-2.5"
          >
            Cancel
          </button>
          <button
            data-testid="copy-yields-confirm"
            onClick={confirm}
            disabled={!chosen}
            className="nb-btn nb-btn-primary px-5 py-2.5 flex items-center gap-2"
          >
            <Copy size={16} strokeWidth={2.5} /> Copy sizes
          </button>
        </div>
      </div>
    </div>
  );
};

export default CopyYieldsDialog;
